import { Image3D } from '~/domain/patient/image3d';
import { cutUID } from '~/utils/string';
import { seriesSummaryOf } from '~/utils/transforms/imageDataTransforms';

/** A human-readable series label for a panel group header. */
export interface SeriesLabel {
    /**
     * Primary label — the series description, or a fallback. May contain PHI,
     * so render it through `SensitiveInfoTypography`.
     */
    readonly label: string;
    /** Secondary label — modality or a UID fragment; null when unavailable. */
    readonly sublabel: string | null;
}

/**
 * Resolve a display label for a series: the name a viewport's chrome gives
 * the image (`seriesSummaryOf`: the description, or `fallbackLabel` when it
 * is empty), with the modality as a sub-label when the label is not already
 * it. When the series is among none of `images`, fall back to the same
 * label plus a short UID fragment so distinct unknown series stay
 * distinguishable.
 *
 * @param seriesInstanceUid Series to label.
 * @param images The images the series may be one of, such as the patient's.
 * @param fallbackLabel Localised "unknown series" text supplied by the caller.
 */
export function resolveSeriesLabel(
    seriesInstanceUid: string,
    images: readonly (Image3D | undefined)[],
    fallbackLabel: string
): SeriesLabel {
    const image = images.find(
        (candidate) => candidate?.seriesUid === seriesInstanceUid
    );
    if (!image) {
        return {
            label: fallbackLabel,
            sublabel: cutUID(seriesInstanceUid, 4, 4) ?? null,
        };
    }
    const { label, modality } = seriesSummaryOf(image, fallbackLabel);
    return { label, sublabel: label === modality ? null : modality };
}
